import forIn from 'lodash/forIn';
import get from 'lodash/get';
import set from 'lodash/set';

import JSTManager from 'gisw/Widgets/JSTManager/JSTManager';

import Factory from './Factory';

const Mediator = {
  /** @type {ServerData} */
  serverData: null,
  /** @type {JSTManager} */
  jstManager: null,
  /** @type {Record<string, Function>} */
  _handlers: {},
  _widgets: [],

  currentUser: '',
  inMobileMode: false,
  showTabletOption: false,
  showDesktopOption: false,
  tabletMode: false,
  desktopMode: false,
  isMobileDevice: false,
  showDesktopMessage: false,
  showDesktopWarning: false,

  ignoreURLTab: false,

  /**
   * @param  {object} config
   * @param {ServerData} config.serverData
   * An object containing data necessary for running the website, e.g.
   * URLs to web services, available tabs, and tools. This data is
   * set by the server in a (cs)html file and passed to the
   * constructor. (see `Views/eCounty/Index.cshtml`)
   */
  constructor (config) {
    this.serverData = config.serverData;

    this.jstManager = new JSTManager();

    // get this out of server data so we can update it when a user logs in
    // (server data is read only and should not be used for dynamic info)
    this.currentUser = this.serverData.currentUser;
    this.layerConfigs = this.serverData.layerConfigs;
    this.isMobile = this._isMobile();

    window.addEventListener('resize', () => {
      this.isMobile = this._isMobile();
    });
  },

  _isMobile () {
    if (/android|ios/.test(navigator.userAgent)) {
      this.inMobileMode = true;
      this.isMobileDevice = true;

      if (this._getWidth() > 900) {
        this.showTabletOption = true;
        this.showDesktopOption = true;
      }

      return true;
    }

    if (this._getWidth() < 1024 && this._getWidth() > 750) {
      this.showDesktopMessage = false;
      this.showDesktopWarning = true;
      this.showTabletOption = true;
      this.showDesktopOption = true;
    }

    if (this._getWidth() <= 750) {
      this.showDesktopWarning = false;
      this.showDesktopMessage = true;
      this.showDesktopOption = false;
      this.showTabletOption = false;
      // this.inMobileMode = true;

      return true;
    }

    return false;
  },

  _getWidth () {
    if (self.innerHeight) {
      return self.innerWidth;
    }

    if (document.documentElement && document.documentElement.clientHeight) {
      return document.documentElement.clientWidth;
    }

    return document.body.clientWidth;
  },

  /**
   * @method Mediator#defineHandler
   * @description Use this method to define more methods for Mediator using
   * strings.  Use "dot" notation to emulate namespacing of actions.
   *
   * @example
   * myMediator.defineHandler("tool.small", function(args) {
   * 		// do something w/ args
   * });
   *
   * // to use...
   * myMediator.handle({
   * 		name: "tool.small",
   * 		args: [arg1,arg2,...]
   * }, this);
   *
   * @param  {string} name
   * Name of method; can use "dot" notation.
   * @param  {function} fn
   * defintion of method specified by name
   * @return {void}
   */
  defineHandler (name, fn) {
    set(this._handlers, name, fn);
  },

  /**
   * @template {import('eCounty/Handlers').HandlerNames} T
   * @param  {object} command
   * @param {T} command.name
   * @param  {import('eCounty/Handlers').HandlerParams<T>} [command.args=[]]
   * @returns {ReturnType<import('eCounty/Handlers').HandlerMap[T]>}
   */
  handle (command) {
    if (!command.hasOwnProperty('name') || typeof command.name !== 'string') {
      return null;
    }

    if (!command.hasOwnProperty('args')) {
      command.args = [];
    } else if (!Array.isArray(command.args)) {
      command.args = [command.args];
    }

    const handler = get(this._handlers, command.name);
    return handler(...command.args);
  },

  /**
   * Define multiple handlers
   * @param  {Record<string, Function>} fnHash
   * each key name is a string that defines a handler function
   * for the action of the same name
   */
  registerHandlers (fnHash) {
    forIn(fnHash, (fn, name) => {
      set(this._handlers, name, fn);
    });
  },

  /**
   * Convenience method for adding properties to a mediator.
   * If `value` has a `startup` method, e.g. if `value` is a widget,
   * then that widget's start method is called automatically.
   * @param {string} name
   * key used to access `value` via `Mediator` instance.
   * @param {any} value item to store
   * @param {boolean} [deleteOnReload]
   */
  store (name, value, deleteOnReload = true) {
    set(this, name, value);

    if (typeof value.startup === 'function') {
      value.startup();
    }

    if (deleteOnReload) {
      this._widgets.push(value);
    }
  },

  destroyStoredWidgets () {
    this._widgets.forEach(w => {
      if (w.domNode) {
        w.domNode.remove();
      }

      this.destroyWidget(w);
    });

    this._widgets = [];
  },

  hasWidgetsToDelete () {
    return this._widgets.length > 0;
  },

  destroyWidget (widget) {
    if (!widget) {
      return;
    }

    dijit.registry.remove(widget.id);

    if (widget.destroyRecursive) {
      widget.destroyRecursive();
    }

    if (widget.destroy) {
      widget.destroy();
    }
  },

  // Defined in `eCounty/Main`
  /** @type {import('gisw/Tools/AlwaysOnIdentify/AlwaysOnIdentify').default} */
  AlwaysOnIdentify: null,
  /** @type {import('eCounty/Widgets/HelpPage').default} */
  helppage: null,
  destroyWidgets: null,
  /** @type {import('gisw/Helpers/MapLayerCredientials').default} */
  mapLayerCredientials: null,
  login: null,
  /** @type {import('eCounty/Widgets/ToolsManager').default} */
  toolsManager: null,
  /** @type {import('eCounty/Widgets/BasemapManager').default} */
  baseMapManager: null,
  /** @type {import('eCounty/Widgets/LayersManager').default} */
  layermanager: null,
  /** @type {import('eCounty/Widgets/CenterMenu').default} */
  centerMenu: null,
  /** @type {import('eCounty/Widgets/RightMenu').default} */
  rightMenu: null,
  /** @type {import('eCounty/Widgets/BottomRightMenu').default} */
  bottomRightMenu: null,
  /** @type {import('gisw/Widgets/GenericSearch/ResultsPanel').default} */
  resultsPanel: null,
  paymentPanel: null,
  CartPanel: null,
  /** @type {import('gisw/Widgets/GWindowList').default} */
  leftPanel: null,
  /** @type {import('gisw/Widgets/GWindowManager').default} */
  gWindowManager: null,

  // Defined in `eCounty/Data`
  getCurrentLayer: null,
  _processLoginData: null,
  initChurnZero: null,
  getCurrentTabName: null,
  getLayersVisibleAtCurrentScale: null,
  getCurrentTabID: null,

  /**
   * @param {string} tabLabel
   */
  getTabNameByLabel (tabLabel) {
    const tabName = tabLabel.replace('<br>', ' ').trim();

    for (const tabLableKey in this.serverData.TabLabel) {
      if (this.serverData.TabLabel[tabLableKey] === tabName) {
        return tabLableKey;
      }
    }

    return tabName;
  },

  getCurrentTabLabel: null,
  getQueryParameter: null,
  CheckSub: null,
  GetLayerIDByName: null,
  GetLayerIDListByName: null,
  RegisterPreserveLayer: null,
  RegisterProtectedLayer: null,
  ClearGraphics: null,
  GetFrontEndOptionIDByName: null,
  /** @type {import('ol/Map').default} */
  map: null,
  /** @type {string} */
  currentTab: null,
  activeTool: null,
  currentToolGroup: null,
  createAndShowPanel: null,
  MapNavExtraTools: [
    'Zoom_In',
    'Zoom_Out',
    'Zoom_To_Previous_Extent',
    'Zoom_To_Next_Extent',
    'Clear_',
    'Full_Extent',
    'Pan',
    'Current_Location'
  ],

  /* eslint-disable camelcase */
  /** @type {Record<FactoryTypes.ModuleNames, string>} */
  ToolsTitleDiscription: {
    MapNavigation: 'Map Nav.',
    Full_Extent: 'Full Extent',
    Zoom_In: 'Zoom In',
    Zoom_Out: 'Zoom Out',
    Zoom_To_Previous_Extent: 'Previous',
    Zoom_To_Next_Extent: 'Next',
    // @ts-ignore
    Clear_: 'Clear',
    BRatio: 'Base Ratio',
    BulkImageUploader: 'Bulk Photos Uploader',
    BookmarkPublic: 'Bookmark',
    Current_Location: 'Current Location',
    WeedsInfestation: 'Weeds Infestation',
    PinDrop: 'Pin Drop',
    PublicNotice: 'Public Notice',
    RoadsProjectManagement: 'Roads Projects',
    ReportBuilder: 'Report Builder',
    TMRM: 'Tablet MRM'
  },
  /* eslint-enable camelcase */
  Factory: new Factory(),
  preserveLayers: null,
  protectedLayers: null
};

if (process.env.NODE_ENV === 'development') {
  // @ts-ignore
  window.mediator = Mediator;
}

export default Mediator;
